import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { GenerationStatus } from '@showbie-socrative/socrative-utils';

import { PaywallScreens } from '~analytics/types';
import { PaywallDialogAiInsights } from '~teacher/components/PaywallDialog';
import { useGetActivityInsights } from '~teacher/hook/activity/insights/useGetActivityInsights';

import { InsightsCompleted } from './InsightsComplete';
import { InsightsFailed } from './InsightsFailed';
import { InsightsInsufficientData } from './InsightsInsufficientData';
import { InsightsLoading } from './InsightsLoading';
import { InsightsNone } from './InsightsNone';
import { InsightsPending } from './InsightsPending';

interface InsightsProps {
  showInsights?: boolean;
  activityInstanceId: number;
  questionIdToNumberMap: Map<string, number>;
  participantUuidToNameMap: Map<string, string>;
  averageScoreRate?: number | null;
}

export function Insights({
  showInsights = false,
  activityInstanceId,
  questionIdToNumberMap,
  participantUuidToNameMap,
  averageScoreRate,
}: InsightsProps) {
  const [showPaywall, setShowPaywall] = useState(false);
  const activityInsightsQuery = useGetActivityInsights({
    activityInstanceId,
    retry: false,
  });
  const generationStatus = activityInsightsQuery.data?.generationStatus;

  const wasPending = useTrackWasPending(generationStatus);

  // Render content based on loading state and generation status
  let content;

  if (activityInsightsQuery.isInitialLoading) {
    content = <InsightsLoading />;
  } else if (!activityInsightsQuery.data) {
    content = (
      <InsightsNone
        activityInstanceId={activityInstanceId}
        showPaywall={showPaywall}
        setShowPaywall={setShowPaywall}
        averageScoreRate={averageScoreRate}
      />
    );
  } else {
    const activityInsights = activityInsightsQuery.data;
    switch (activityInsights.generationStatus) {
      case GenerationStatus.Pending:
        content = <InsightsPending activityInstanceId={activityInstanceId} />;
        break;

      case GenerationStatus.Completed:
        content = (
          <InsightsCompleted
            activityInstanceId={activityInstanceId}
            insights={activityInsights}
            questionIdToNumberMap={questionIdToNumberMap}
            participantUuidToNameMap={participantUuidToNameMap}
            showPaywall={showPaywall}
            setShowPaywall={setShowPaywall}
            averageScoreRate={averageScoreRate}
            defaultOpen={wasPending || showInsights}
          />
        );
        break;

      case GenerationStatus.Failed:
        content = (
          <InsightsFailed
            activityInstanceId={activityInstanceId}
            showPaywall={showPaywall}
            setShowPaywall={setShowPaywall}
          />
        );
        break;

      case GenerationStatus.InsufficientData:
        content = (
          <InsightsInsufficientData
            activityInstanceId={activityInstanceId}
            showPaywall={showPaywall}
            setShowPaywall={setShowPaywall}
            averageScoreRate={averageScoreRate}
          />
        );
        break;

      default:
        content = (
          <InsightsNone
            activityInstanceId={activityInstanceId}
            showPaywall={showPaywall}
            setShowPaywall={setShowPaywall}
            averageScoreRate={averageScoreRate}
          />
        );
    }
  }

  return (
    <>
      {content}
      <PaywallDialogAiInsights
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        paywallScreen={PaywallScreens.AiInsightsLimit}
      />
    </>
  );
}

/**
 * To track if the generation was pending during the component lifecycle
 */
function useTrackWasPending(generationStatus: GenerationStatus) {
  const wasPendingRef = useRef(false);
  // Track if generation was pending during component lifecycle
  useEffect(() => {
    if (generationStatus === GenerationStatus.Pending) {
      wasPendingRef.current = true;
    }
  }, [generationStatus]);
  return wasPendingRef.current;
}
